import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

import { clearOrderData } from 'hooks/orderForms/useOrderData';
import { logError } from 'services/logging/logging';
import { useMessage } from 'services/repositories/messages/messages';
import { downloadAsPDF, useSenderEnvelope } from 'services/repositories/transactions/transactions';
import { useCurrentUser } from 'services/repositories/user/user';
import { formatInstantAsDay } from 'support/helpers/dateTime/dateTime';
import { RouteParams } from 'support/helpers/types/types';
import { OrderFormsRoutes } from 'support/types';
import { Button } from 'components/Form/Button/Button';
import { OrderFormPageLayout } from 'components/OrderFormPageLayout/OrderFormPageLayout';

import { OrderSummaryCard } from './components/OrderSummaryCard/OrderSummaryCard';
import { PoweredByFooter } from './components/PoweredByFooter/PoweredByFooter';
import { SuccessIcon } from './components/SuccessIcon/SuccessIcon';
import { ThankYouMessage } from './components/ThankYouMessage/ThankYouMessage';

export const OrderFormSuccess = ({ type }: { type: 'sender-envelope' | 'message' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMessage = type === 'message';
  const { id, supplierSlug } = useParams<
    RouteParams<OrderFormsRoutes.successMessage> | RouteParams<OrderFormsRoutes.successSenderEnvelope>
  >();
  const { data: message, isLoading: isLoadingMessage } = useMessage({
    variables: { messageId: id },
    enabled: isMessage && !!id,
  });

  const { data: senderEnvelope, isLoading: isLoadingSenderEnvelope } = useSenderEnvelope({
    variables: { senderEnvelopeId: id },
    enabled: !isMessage && !!id,
  });

  const { data: currentUser, isLoading: isLoadingCurrentUser } = useCurrentUser();
  const isLoading = isLoadingCurrentUser || (isMessage ? isLoadingMessage : isLoadingSenderEnvelope);

  const handleDownloadPDF = () => {
    if (!message?.data) {
      logError('OrderForm: handleDownloadPDF: No message data found');
      return;
    }
    downloadAsPDF(message.data);
  };

  const handleStartAnotherOrder = () => {
    if (!supplierSlug) {
      logError('OrderForm: handleStartAnotherOrder: No supplier slug found');
      return;
    }
    clearOrderData(supplierSlug);
    navigate(generatePath(OrderFormsRoutes.root, { supplierSlug }), { replace: true });
  };

  const externalMessageRef = isMessage
    ? message?.data.external_message_ref
    : senderEnvelope?.transaction.externalMessageRef;

  const orderDate = isMessage
    ? message?.data.created_at
      ? formatInstantAsDay(message.data.created_at)
      : undefined
    : senderEnvelope?.transaction.createdAt
      ? formatInstantAsDay(senderEnvelope.transaction.createdAt)
      : undefined;

  return (
    <OrderFormPageLayout isLoading={isLoading}>
      <div className="flex flex-col items-center gap-24">
        <div className="flex flex-col items-center gap-10">
          <div className="flex max-w-96 flex-col items-center gap-6">
            <SuccessIcon />
            <ThankYouMessage email={currentUser?.email} />
          </div>
          <OrderSummaryCard orderNumber={externalMessageRef || undefined} orderDate={orderDate} />
          <div className="flex flex-col items-center gap-6">
            {isMessage && (
              <Button
                onClick={handleDownloadPDF}
                variant="primary"
                size="extra-large"
                LeftIcon={ArrowDownTrayIcon}
                analyticsId="order-success:download-pdf"
              >
                {t('orderForms:success.downloadPdf')}
              </Button>
            )}
            <Button
              onClick={handleStartAnotherOrder}
              variant="secondary"
              size="extra-large"
              analyticsId="order-success:start-another-order"
            >
              {t('orderForms:success.startAnotherOrder')}
            </Button>
          </div>
        </div>
        <PoweredByFooter />
      </div>
    </OrderFormPageLayout>
  );
};
